/**
 * The words the sign-in screen (portal) and the invitation page (API) both show, in one place so they cannot differ
 * (#1933). They are the words of the design John approved on 7 Oct. The screen carries no plan or COO-pricing copy:
 * the approved design bars it.
 */
export interface AuthScreenCopy {
    /** The sign-in screen's one h1. */
    title: string
    /** The line under the title. */
    subtitle: string
    /** Under "Continue with email". */
    emailHelper: string
    /** The legal line; "Product Terms" and "Privacy Policy" in it are links. Continuing accepts the product terms. */
    footer: string
    /** The splash panel's h2. */
    splashHeadline: string
    /** The splash example card: who is asking. */
    exampleWho: string
    /** The splash example card's status pill. */
    examplePill: string
    /** The splash example card's message. */
    exampleText: string
    /** The splash example card's (inert) outline button. */
    exampleApprove: string
    /** The splash example card's (inert) ghost button. */
    exampleRequestChanges: string
    /** The splash's finished activity row. */
    activityDone: string
    /** The splash's in-progress activity row. */
    activityDoing: string
    /** Under "Accept with email" on the invitation page. */
    invitationHelper: string
}

export const AUTH_SCREEN_COPY: AuthScreenCopy = {
    title: 'Sign in or create your account',
    subtitle: 'Get your AI team working in minutes.',
    emailHelper: "We'll email you a one-time code. No password needed.",
    footer: 'By continuing you agree to our Product Terms and Privacy Policy.',
    splashHeadline: 'Your company, run by an AI team you approve.',
    exampleWho: 'Your COO',
    examplePill: 'Needs you',
    exampleText: "I've drafted this week's customer update. Approve it and I'll send it.",
    exampleApprove: 'Approve',
    exampleRequestChanges: 'Request changes',
    activityDone: 'Engineer finished: landing page copy',
    activityDoing: 'Assistant is booking your Thursday calls',
    invitationHelper: "Use any option above or the invited email. You'll get exactly the access you were invited with.",
}

/**
 * Where the footer's "Product Terms" and "Privacy Policy" go: the public site's legal pages. Signing up accepts the
 * product terms in force (the founding records it), so the link is the current version's page.
 */
export const AUTH_SCREEN_LINKS = {
    terms: 'https://zerohuman.com/legal/product-terms',
    privacy: 'https://zerohuman.com/legal/privacy',
} as const

/** One run of the footer line: plain words, or words that are a link. */
export type AuthFooterPart = { text: string; href?: string }

const FOOTER_LINKS: Readonly<Record<string, string>> = {
    'Product Terms': AUTH_SCREEN_LINKS.terms,
    'Privacy Policy': AUTH_SCREEN_LINKS.privacy,
}

/**
 * The footer line in order, with "Product Terms" and "Privacy Policy" as links, so the portal and the invitation page render
 * the one sentence the same way and neither restates it in pieces.
 */
export function authFooterParts(): AuthFooterPart[] {
    return AUTH_SCREEN_COPY.footer
        .split(/(Product Terms|Privacy Policy)/)
        .filter(text => text !== '')
        .map(text => {
            const href = FOOTER_LINKS[text]
            return href ? { text, href } : { text }
        })
}
